import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { staffService } from '../../services';
import { useAuth } from '../../context/AuthContext';
import { isOwnerRole, isManagerRole } from '../../config/menuCodeMap';
import {
  Plus, Clock, ShieldAlert, Search, Eye, Filter, MoreVertical,
  Edit2, Archive, ArchiveRestore, KeyRound, UserCheck, UserX, CheckCircle2, XCircle,
  LogOut, Undo2, KeySquare, Building2, Smartphone,
} from 'lucide-react';
import { SlideOver, Pagination, DataTable, Button, PageHeader, Select, UserPhoto, Loader, Modal } from '../../components';
import { toast } from 'react-hot-toast';
import StaffDetails from './StaffDetails';
import ManageRosterModal from './ManageRosterModal';
import ManageAccessModal from './ManageAccessModal';
import AppUsagePanel from './AppUsagePanel';

/**
 * Staff Management
 * All / Archived tabs, role + status filters, search, and per-row actions
 * (view + a 3-dot menu: edit, activate/deactivate, mark leaver/returned,
 * archive/restore, reset PIN, manage roster, manage access, approve/reject,
 * and — on carer rows, for managers and owners — view app usage).
 *
 * The main tab lists EVERY non-archived member — active, inactive and leavers —
 * and only narrows when a status filter is chosen. Leavers stay listed (with
 * their leaving date) so their history remains visible; archiving is the
 * separate action that hides someone.
 */

// Tab title for each status filter — '' (no filter) lists everyone in the tab.
const STATUS_TITLES = {
  '': 'All Staff',
  active: 'Active Staff',
  inactive: 'Inactive Staff',
  leaver: 'Leavers',
};

// "2026-07-31" → "31 Jul 2026" for the leaving date under the status badge.
const fmtLeftOn = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Deterministic soft avatar colour from the name, so each person keeps a stable tint.
const AVATAR_TINTS = [
  'bg-purple-100 text-purple-700',
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-indigo-100 text-indigo-700',
  'bg-cyan-100 text-cyan-700',
];
const tintFor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h + name.charCodeAt(i)) % AVATAR_TINTS.length;
  return AVATAR_TINTS[h];
};
const initials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
};

const StaffList = () => {
  const navigate = useNavigate();
  const { user, sites, activeSiteId } = useAuth();
  // Multi-site groups: the owner can move somebody to another of their sites.
  const otherSites = (sites || []).filter((site) => Number(site.id) !== Number(activeSiteId));
  const canMoveSites = isOwnerRole(user) && otherSites.length > 0;
  const [moveFor, setMoveFor] = useState(null);
  const [moveTo, setMoveTo] = useState('');
  const [moving, setMoving] = useState(false);
  const [staff, setStaff] = useState([]);
  const [counts, setCounts] = useState({ active: 0, archived: 0, leavers: 0 });
  // Total rows matching the CURRENT filters — drives the count next to the tab
  // title so it agrees with what's actually listed.
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  // '' = everyone, 'carer' = uses the mobile app (role 3 or 5, whichever this
  // organisation uses), 'staff' = dashboard only (role 6). See the API's
  // getAllStaff — it matches the two sides of the Carer toggle, not a role id.
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '', active, inactive
  const [tab, setTab] = useState('active'); // 'active' | 'archived'

  /* ── The staff record, in a slide-over ──────────────────────────────────
   * The record opens beside the list rather than replacing it: a manager
   * checking who has what reads one person, closes, and opens the next
   * without losing their filters, their page or their place in the table.
   *
   * The full page at /manager/staffs/:id/view still exists for a bookmarked
   * link and for where Recruitment lands a transferred candidate. Both render
   * StaffDetails, so there is one record to maintain.
   * ──────────────────────────────────────────────────────────────────────── */
  const [viewing, setViewing] = useState(null);   // the row that was clicked
  const [viewStaff, setViewStaff] = useState(null); // the full record
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);

  const openStaffPanel = async (member) => {
    // Show the panel immediately with what the row already knows, so the name
    // and id are there while the rest loads — a spinner over a blank panel
    // reads as slower than it is.
    setViewing(member);
    setViewStaff(null);
    setViewError(null);
    setViewLoading(true);
    try {
      const res = await staffService.getStaffById(member.id);
      if (res.success) setViewStaff(res.data);
      else setViewError(res.message || 'Could not load this staff member');
    } catch (err) {
      setViewError(err.message || 'Could not load this staff member');
    } finally {
      setViewLoading(false);
    }
  };

  const closeStaffPanel = () => {
    setViewing(null);
    setViewStaff(null);
    setViewError(null);
  };

  // 3-dot dropdown — anchored with fixed coords so the card's overflow can't clip it.
  const [menu, setMenu] = useState(null); // { id, x, y }
  const [actioningId, setActioningId] = useState(null);

  // Roster and access are edited in their own dialogs, each holding the one
  // staff member it was opened for. Both load that person's CURRENT settings
  // when they open rather than reading the list row, which carries neither.
  const [rosterFor, setRosterFor] = useState(null);
  const [accessFor, setAccessFor] = useState(null);
  const [groups, setGroups] = useState([]);

  // How long a carer had the mobile app open. Offered on carer rows only, and
  // only to managers and owners — the API refuses anybody else.
  const [usageFor, setUsageFor] = useState(null);
  const canSeeAppUsage = isManagerRole(user);
  const isCarerRow = (m) => (m.is_carer !== undefined
    ? m.is_carer === true
    : [3, 5].includes(Number(m.role_id)));

  // A domiciliary service records availability; a care home rosters shifts.
  // Read from the organisation, the same axis Add Staff branches on.
  const isDomiciliaryOrg = (user?.organization?.organization_type || '').toUpperCase() === 'DOMICILIARY';

  const LIMIT = 10;

  const fetchStaff = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await staffService.getAllStaff(
        page, LIMIT, submittedSearch, roleFilter, statusFilter, tab === 'archived',
      );
      if (response.success) {
        setStaff(response.data || []);
        if (response.counts) setCounts(response.counts);
        setTotalPages(response.pagination.pages || 1);
        setCurrentPage(response.pagination.page || 1);
        setFilteredTotal(response.pagination.total ?? 0);
      } else {
        setError(response.message || 'Failed to fetch staff');
      }
    } catch (err) {
      setError(err.message || 'Error fetching staff');
    } finally {
      setLoading(false);
    }
  }, [submittedSearch, roleFilter, statusFilter, tab]);

  useEffect(() => { fetchStaff(currentPage); }, [currentPage, fetchStaff]);
  useEffect(() => { fetchGroups(); }, []);

  const fetchGroups = async () => {
    try {
      const response = await staffService.getStaffGroups();
      if (response.success) setGroups(response.data || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    setSubmittedSearch(search);
    setCurrentPage(1);
  };

  const switchTab = (t) => {
    if (t === tab) return;
    setTab(t);
    setStatusFilter('');
    setCurrentPage(1);
  };

  // ── Row actions ────────────────────────────────────────────────────────────
  const closeMenu = () => setMenu(null);

  const openMenu = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    // `anchorTop` is kept so the menu can flip above the button when there
    // isn't room below it (the last rows of the list) — see useLayoutEffect.
    setMenu({ id, x: rect.right, y: rect.bottom + 6, anchorTop: rect.top });
  };

  // Keep the dropdown on screen. Measured after render but before paint, so
  // the flip never flashes: below the button by default, above it when the
  // menu would run past the bottom of the window, and clamped inside the
  // window if neither side has room.
  const menuRef = useRef(null);
  const [menuTop, setMenuTop] = useState(null);
  useLayoutEffect(() => {
    if (!menu || !menuRef.current) { setMenuTop(null); return; }
    const GAP = 6;
    const EDGE = 12;
    const h = menuRef.current.offsetHeight;
    const vh = window.innerHeight;
    let top = menu.y;
    if (top + h > vh - EDGE) {
      const above = menu.anchorTop - GAP - h;
      top = above >= EDGE ? above : Math.max(EDGE, vh - EDGE - h);
    }
    setMenuTop(top);
  }, [menu]);

  // A fixed menu doesn't follow the page, so close it on scroll / resize
  // rather than leave it floating away from its row.
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [menu]);

  const handleToggleStatus = async (member) => {
    closeMenu();
    try {
      setActioningId(member.id);
      const res = await staffService.setStaffStatus(member.id, !member.is_active);
      if (res.success) {
        toast.success(member.is_active ? 'Marked inactive' : 'Reactivated');
        fetchStaff(currentPage);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update status');
    } finally {
      setActioningId(null);
    }
  };

  // Mark someone as having left the organisation, or bring them back. Confirmed
  // because it also revokes their sign-in.
  const handleToggleLeaver = async (member) => {
    closeMenu();
    const leaving = !member.is_leaver;
    if (leaving && !window.confirm(
      `Mark ${member.name} as a leaver? They stay in the list with their history, but can no longer sign in.`
    )) return;

    try {
      setActioningId(member.id);
      const res = await staffService.setStaffLeaver(member.id, leaving);
      if (res.success) {
        toast.success(res.message || (leaving ? 'Marked as leaver' : 'Restored as active staff'));
        fetchStaff(currentPage);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update leaver status');
    } finally {
      setActioningId(null);
    }
  };

  const handleArchive = async (member) => {
    closeMenu();
    try {
      setActioningId(member.id);
      const res = await staffService.archiveStaff(member.id, tab !== 'archived');
      if (res.success) {
        toast.success(tab === 'archived' ? 'Staff restored' : 'Staff archived');
        fetchStaff(currentPage);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to archive');
    } finally {
      setActioningId(null);
    }
  };

  const handleResetPassword = async (member) => {
    closeMenu();
    try {
      setActioningId(member.id);
      const res = await staffService.resetStaffPassword(member.id);
      if (res.success) toast.success(res.message || 'Reset link sent');
    } catch (err) {
      toast.error(err.message || 'Failed to send reset email');
    } finally {
      setActioningId(null);
    }
  };

  // Both dialogs are handed the row and nothing else. They fetch that person's
  // real roster or access when they open, because the list row carries a
  // summary of neither — and a dialog seeded from a summary saves a guess back
  // over the real thing.
  const openRosterFor = (member) => { closeMenu(); setRosterFor(member); };
  const openAccessFor = (member) => { closeMenu(); setAccessFor(member); };
  const openMoveFor = (member) => { closeMenu(); setMoveTo(''); setMoveFor(member); };

  const handleMoveSite = async () => {
    if (!moveFor || !moveTo) return;
    try {
      setMoving(true);
      const res = await staffService.moveStaffToSite(moveFor.id, Number(moveTo));
      if (res.success) {
        toast.success(res.message || 'Moved to the other site');
        setMoveFor(null);
        fetchStaff(currentPage);
      } else {
        toast.error(res.message || 'Could not move them');
      }
    } catch (err) {
      toast.error(err.message || 'Could not move them');
    } finally {
      setMoving(false);
    }
  };

  const handleApprove = async (member) => {
    closeMenu();
    try {
      const res = await staffService.approveStaff(member.id);
      if (res.success) {
        // No roster prompt here. A transferred candidate already had their
        // roster set during the transfer, and anyone who still needs one can be
        // given it from this row's "Manage roster" action.
        toast.success(`${member.name} approved`);
        fetchStaff(currentPage);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to approve');
    }
  };

  const handleReject = async (member) => {
    closeMenu();
    if (!window.confirm(`Reject ${member.name}?`)) return;
    try {
      const res = await staffService.rejectStaff(member.id);
      if (res.success) { toast.success(`${member.name} rejected`); fetchStaff(currentPage); }
    } catch (err) {
      toast.error(err.message || 'Failed to reject');
    }
  };

  const menuMember = menu ? staff.find((s) => s.id === menu.id) : null;

  // Empty-state messaging (filter-aware) for the table.
  const hasFilters = submittedSearch || roleFilter || statusFilter;
  const staffEmptyMsg = tab === 'archived'
    ? 'No archived staff. Archive a member from the Active tab to see them here.'
    : hasFilters
      ? "We couldn't find anyone matching your current filters."
      : "You haven't added any staff members yet.";
  const staffShowClear = hasFilters && tab === 'active';
  const clearStaffFilters = () => { setSearch(''); setSubmittedSearch(''); setRoleFilter(''); setStatusFilter(''); };

  // Table columns — same cells as before, now driven by DataTable.
  const staffColumns = [
    {
      key: 'name', header: 'Staff',
      render: (member) => (
        <div className="flex items-center gap-3">
          <UserPhoto
            src={member.avatar_url}
            alt={member.name}
            className="w-9 h-9 rounded-full"
            fallback={(
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-black shrink-0 ${tintFor(member.name)}`}>
                {initials(member.name)}
              </div>
            )}
          />
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-100 truncate">{member.name}</div>
            <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 font-mono truncate">
              {member.user_id}
              {/* Only ever set in an account owner's directory. */}
              {member.is_manager && (
                <span className="ml-2 font-sans text-[10px] font-bold text-[#2563EB] bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded">Manager</span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      // THE JOB TITLE — what this person is employed as, the field Add & Edit
      // Staff sets under Employment details.
      //
      // This column used to stack the job role over a Carer / Manager line.
      // Job role and job title were two fields for one thing, so the role is
      // gone; the Carer / Manager line went with it because two values under a
      // single heading read as two job titles. The toolbar's Role filter still
      // filters by carer-vs-manager.
      key: 'role', header: 'Job title',
      render: (member) => (
        <div className="min-w-0 text-[13px] text-[#1E293B] dark:text-gray-200 font-medium break-words">
          {member.job_title || '—'}
        </div>
      ),
    },
    {
      key: 'contact', header: 'Email / Phone',
      render: (member) => (
        <>
          <div className="text-[13px] text-[#475569] dark:text-gray-300">{member.email || '-'}</div>
          <div className="text-[12px] text-[#94A3B8] dark:text-gray-500">{member.phone || '-'}</div>
        </>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (member) => (
        <div className="flex flex-col gap-1.5">
          {/* Leaver takes precedence — someone who has left the organisation is
              always inactive, and "Leaver" is the more meaningful label. */}
          {member.is_leaver ? (
            <>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold w-fit bg-rose-50 dark:bg-rose-900/10 text-rose-700 dark:text-rose-400">
                <LogOut className="w-3 h-3" />
                Leaver
              </span>
              {member.left_on && (
                <span className="text-[11px] text-[#94A3B8] dark:text-gray-500">
                  Left {fmtLeftOn(member.left_on)}
                </span>
              )}
            </>
          ) : (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold w-fit ${
              member.is_active ? 'bg-green-50 dark:bg-emerald-900/10 text-green-700 dark:text-emerald-400' : 'bg-slate-100 dark:bg-[#31373f] text-slate-500 dark:text-gray-400'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${member.is_active ? 'bg-green-500' : 'bg-slate-400'}`} />
              {member.is_active ? 'Active' : 'Inactive'}
            </span>
          )}
          {member.approval_status === 'pending' && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600">
              <ShieldAlert className="w-3 h-3" /> Pending approval
            </span>
          )}
          {/* A leaver doesn't need a roster — don't nag about one. */}
          {member.approval_status === 'approved' && !member.rosterDetails
            && !member.is_leaver && tab === 'active' && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-500">
              <ShieldAlert className="w-3 h-3" /> Roster needed
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'actions', header: 'Actions', align: 'right',
      render: (member) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => openStaffPanel(member)}
            className="p-1.5 rounded-lg text-[#64748B] dark:text-gray-400 hover:text-[#2563EB] hover:bg-blue-50 transition-colors"
            title="View"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => openMenu(e, member.id)}
            className={`p-1.5 rounded-lg text-[#64748B] dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#31373f] transition-colors ${menu?.id === member.id ? 'bg-gray-100 dark:bg-[#31373f]' : ''}`}
            title="More actions"
            disabled={actioningId === member.id}
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full space-y-6 pb-8">
      {/* Header */}
      {/* "Add Staff" creates an account directly, bypassing recruitment. The
          usual route for a new starter is Staff -> Recruitment & Onboarding
          (the candidate fills in their own details and the account is created on
          transfer), but this stays available for the cases that don't fit —
          transfers, bank staff, or backfilling someone already working. */}
      <PageHeader
        title="Staff Management"
        subtitle="Manage your staff members and their permissions"
        actions={(
          <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/manager/staffs/create')}>
            Add Staff
          </Button>
        )}
      />

      <div className="bg-white dark:bg-[#262b33] rounded-[16px] shadow-sm border border-[#E2E8F0] dark:border-gray-800">
        {/* Tabs */}
        <div className="px-4 pt-4 border-b border-[#E2E8F0] dark:border-gray-800">
          <div className="flex items-center gap-6">
            {[
              // The title follows the status filter — "All Staff" by default,
              // then "Active Staff" / "Inactive Staff" / "Leavers" — so the
              // heading always describes what's actually in the table. The count
              // follows the filter too, rather than the tab total.
              {
                key: 'active',
                label: STATUS_TITLES[statusFilter] || STATUS_TITLES[''],
                count: statusFilter ? filteredTotal : counts.active,
              },
              { key: 'archived', label: 'Archived Staff', count: counts.archived },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => switchTab(t.key)}
                className={`relative pb-3 text-[14px] font-semibold transition-colors ${
                  tab === t.key ? 'text-[#2563EB] dark:text-blue-400' : 'text-[#64748B] dark:text-gray-400 hover:text-[#1E293B] dark:hover:text-gray-200'
                }`}
              >
                {t.label} ({t.count})
                {tab === t.key && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#2563EB] rounded-full" />}
              </button>
            ))}
          </div>
        </div>

        {/* Toolbar */}
        <div className="p-4 flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Role filter */}
            <Select
              className="w-full sm:w-[170px]"
              size="sm"
              leftIcon={<Filter className="w-4 h-4" />}
              value={roleFilter}
              onChange={(v) => { setRoleFilter(v); setCurrentPage(1); }}
              // An account owner's directory also holds real managers, so for
              // them dashboard-only staff are called what they are.
              options={isOwnerRole(user) ? [
                { value: '', label: 'All Roles' },
                { value: 'manager', label: 'Manager' },
                { value: 'staff', label: 'Office staff' },
                { value: 'carer', label: 'Carer' },
              ] : [
                { value: '', label: 'All Roles' },
                { value: 'staff', label: 'Manager' },
                { value: 'carer', label: 'Carer' },
              ]}
            />

            {/* Status filter — only meaningful in the Active tab */}
            {tab === 'active' && (
              <Select
                className="w-full sm:w-[170px]"
                size="sm"
                leftIcon={<span className="w-2 h-2 rounded-full bg-[#94A3B8] inline-block" />}
                value={statusFilter}
                onChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'leaver', label: 'Leaver' },
                ]}
              />
            )}
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex items-center gap-2 w-full lg:w-auto">
            <div className="relative w-full sm:w-[320px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <input
                type="text"
                placeholder="Search staff..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-[#CBD5E1] dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB] text-[13px] text-[#0F172A] dark:text-gray-200 bg-[#F8FAFC] dark:bg-[#31373f]"
              />
            </div>
            <Button type="submit" variant="primary" icon={<Search className="w-4 h-4" />} aria-label="Search" />
          </form>
        </div>

        {/* Table */}
        {error ? (
          <div className="p-12 text-center">
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/50 text-red-600 dark:text-red-400 px-6 py-4 rounded-xl inline-block">{error}</div>
            <button onClick={() => fetchStaff(currentPage)} className="block mx-auto mt-4 text-sm text-blue-600 font-medium hover:underline">Try Again</button>
          </div>
        ) : (
          <DataTable
            bare
            columns={staffColumns}
            data={staff}
            loading={loading}
            rowKey={(m) => m.id}
            emptyIcon={Search}
            emptyTitle="No staff members found"
            emptyText={staffEmptyMsg}
            emptyAction={staffShowClear ? (
              <Button variant="ghost" size="sm" onClick={clearStaffFilters}>Clear all filters</Button>
            ) : undefined}
          />
        )}

        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </div>

      {/* 3-dot dropdown (fixed, so the card's rounded overflow can't clip it) */}
      {menu && menuMember && (
        <>
          <div className="fixed inset-0 z-40" onClick={closeMenu} />
          <div
            ref={menuRef}
            className="fixed z-50 w-52 bg-white dark:bg-[#262b33] rounded-xl shadow-xl border border-[#E2E8F0] dark:border-gray-800 py-1.5 animate-in fade-in zoom-in-95 duration-100"
            style={{
              top: menuTop ?? menu.y,
              left: Math.max(12, menu.x - 208),
              // Hidden for the one pre-paint pass before it's measured.
              visibility: menuTop === null ? 'hidden' : 'visible',
            }}
          >
            <MenuItem icon={Edit2} label="Edit Staff" onClick={() => { closeMenu(); navigate(`/manager/staffs/${menuMember.id}/edit`); }} />

            {menuMember.approval_status === 'pending' && (
              <>
                <MenuItem icon={CheckCircle2} label="Approve" tone="text-green-600" onClick={() => handleApprove(menuMember)} />
                <MenuItem icon={XCircle} label="Reject" tone="text-orange-600" onClick={() => handleReject(menuMember)} />
              </>
            )}

            <MenuItem icon={Clock} label="Manage roster" onClick={() => openRosterFor(menuMember)} />
            <MenuItem icon={KeySquare} label="Manage access" onClick={() => openAccessFor(menuMember)} />
            {canSeeAppUsage && isCarerRow(menuMember) && (
              <MenuItem icon={Smartphone} label="View app usage" onClick={() => { closeMenu(); setUsageFor(menuMember); }} />
            )}
            {canMoveSites && (
              <MenuItem icon={Building2} label="Move to another site" onClick={() => openMoveFor(menuMember)} />
            )}

            {/* Active/Inactive is a temporary toggle, so it's meaningless for
                someone who has left — only offer the return action there. */}
            {!menuMember.is_leaver && (
              menuMember.is_active ? (
                <MenuItem icon={UserX} label="Mark as Inactive" onClick={() => handleToggleStatus(menuMember)} />
              ) : (
                <MenuItem icon={UserCheck} label="Reactivate" onClick={() => handleToggleStatus(menuMember)} />
              )
            )}

            {menuMember.is_leaver ? (
              <MenuItem icon={Undo2} label="Mark as Returned" tone="text-green-600" onClick={() => handleToggleLeaver(menuMember)} />
            ) : (
              <MenuItem icon={LogOut} label="Mark as Leaver" tone="text-rose-600" onClick={() => handleToggleLeaver(menuMember)} />
            )}

            {tab === 'archived' ? (
              <MenuItem icon={ArchiveRestore} label="Restore Staff" onClick={() => handleArchive(menuMember)} />
            ) : (
              <MenuItem icon={Archive} label="Archive Staff" onClick={() => handleArchive(menuMember)} />
            )}

            <MenuItem icon={KeyRound} label="Reset Password" onClick={() => handleResetPassword(menuMember)} />
          </div>
        </>
      )}

      {/* Roster and access, each on its own. Both are read afresh for the one
          person they were opened for — see ManageRosterModal / ManageAccessModal. */}
      <ManageRosterModal
        isOpen={Boolean(rosterFor)}
        onClose={() => setRosterFor(null)}
        staff={rosterFor}
        groups={groups}
        domiciliary={isDomiciliaryOrg}
        onGroupCreated={fetchGroups}
        onSaved={() => fetchStaff(currentPage)}
      />

      <ManageAccessModal
        isOpen={Boolean(accessFor)}
        onClose={() => setAccessFor(null)}
        staff={accessFor}
        onSaved={() => fetchStaff(currentPage)}
      />

      <AppUsagePanel staff={usageFor} onClose={() => setUsageFor(null)} />

      {/* Multi-site groups: move somebody to another site (owner only). */}
      <Modal
        isOpen={Boolean(moveFor)}
        onClose={() => !moving && setMoveFor(null)}
        title={`Move ${moveFor?.name || ''} to another site`}
        maxWidth="max-w-lg"
        footer={(
          <div className="flex justify-end gap-2.5">
            <Button variant="secondary" onClick={() => setMoveFor(null)} disabled={moving}>Cancel</Button>
            <Button variant="primary" loading={moving} disabled={!moveTo} onClick={handleMoveSite}>
              Move
            </Button>
          </div>
        )}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">New site</label>
            <Select
              value={moveTo}
              onChange={(v) => setMoveTo(v)}
              placeholder="Choose a site"
              options={otherSites.map((site) => ({ value: String(site.id), label: site.name }))}
              className="w-full"
            />
          </div>
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 leading-relaxed">
            Their compliance checks, leave allowance, leave requests and availability move with them,
            and they are taken off their team here. Shifts, visits, care notes and training stay
            with this site, where they happened.
          </p>
        </div>
      </Modal>

      {/* The staff record, beside the list rather than instead of it. */}
      <SlideOver
        isOpen={!!viewing}
        onClose={closeStaffPanel}
        width="sm:max-w-[640px]"
        title={(
          <div className="flex items-center gap-3 min-w-0">
            <UserPhoto
              src={viewStaff?.avatar_url ?? viewing?.avatar_url}
              alt={viewing?.name}
              className="w-9 h-9 rounded-full shrink-0"
              fallback={(
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/40 dark:to-indigo-900/40 text-[#2563EB] dark:text-blue-300 text-[12px] font-black flex items-center justify-center shrink-0">
                  {initials(viewing?.name)}
                </div>
              )}
            />
            <span className="truncate">{viewing?.name}</span>
          </div>
        )}
        subtitle={viewing && (
          <span className="flex items-center gap-2 flex-wrap">
            <span className="font-mono">{viewing.user_id}</span>
            <span className="text-[#CBD5E1] dark:text-gray-600">·</span>
            <span>
              {viewing.is_manager
                ? 'Manager'
                : (viewing.is_carer !== undefined
                  ? viewing.is_carer === true
                  : [3, 5].includes(Number(viewing.role_id)))
                  ? 'Carer'
                  : (isOwnerRole(user) ? 'Office staff' : 'Manager')}
            </span>
            <span className="text-[#CBD5E1] dark:text-gray-600">·</span>
            <span className={viewing.is_active
              ? 'text-emerald-600 dark:text-emerald-400 font-semibold'
              : 'text-[#94A3B8] dark:text-gray-500 font-semibold'}>
              {viewing.is_active ? 'Active' : 'Inactive'}
            </span>
          </span>
        )}
        footer={viewing && (
          /* The panel is the record — there is no "open full page" escape,
             because everything the page showed is here. Edit is the only thing
             left to do from it. */
          <div className="flex items-center justify-end gap-3">
            <Button variant="secondary" onClick={closeStaffPanel}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate(`/manager/staffs/${viewing.id}/edit`)}
            >
              Edit staff
            </Button>
          </div>
        )}
      >
        {viewLoading && !viewStaff ? (
          <div className="py-16">
            <Loader text="Loading record..." />
          </div>
        ) : viewError ? (
          <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/50 text-red-600 dark:text-red-400 px-4 py-3 rounded-xl text-[13px] font-medium">
            {viewError}
          </div>
        ) : (
          <StaffDetails staff={viewStaff} dense />
        )}
      </SlideOver>
    </div>
  );
};

// Single row inside the 3-dot dropdown.
const MenuItem = ({ icon: Icon, label, onClick, tone }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium hover:bg-[#F8FAFC] dark:hover:bg-[#31373f] transition-colors ${tone || 'text-[#475569] dark:text-gray-300'}`}
  >
    <Icon className="w-4 h-4 shrink-0" />
    {label}
  </button>
);

export default StaffList;
